import { NOTIFICATION_STATUS } from '../../domain/pwa/notificationOffer';

/**
 * What this browser will let us do about notifications, and asking it (SPEC-016).
 *
 * Every access is guarded, because "does `Notification` exist" has three answers
 * in the wild and only two of them are the obvious ones. It is absent in iOS
 * Safari in a browser TAB and present in the same Safari once the app is on the
 * home screen; it exists but throws on `permission` in some embedded and
 * privacy-hardened engines. An unguarded read of any of it takes the shell down
 * on first paint, which is a steep price for a card the visitor never asked for.
 *
 * The API arrives as a defaulted parameter so every branch is testable in jsdom,
 * where `Notification` does not exist at all.
 */

function defaultNotificationApi() {
    return typeof Notification === 'undefined' ? null : Notification;
}

export function getPlantNotificationCapability(notificationApi = defaultNotificationApi()) {
    if (!notificationApi || typeof notificationApi.requestPermission !== 'function') {
        return { isSupported: false, permission: null, status: NOTIFICATION_STATUS.unsupported };
    }

    try {
        const permission = typeof notificationApi.permission === 'string'
            ? notificationApi.permission
            : NOTIFICATION_STATUS.default;

        return { isSupported: true, permission, status: permission };
    } catch (error) {
        return { isSupported: true, permission: null, status: NOTIFICATION_STATUS.error };
    }
}

/**
 * Ask, once.
 *
 * Only ever called from a click — the browser requires a user gesture, and more
 * to the point an unprompted ask is the thing this whole feature is built to
 * avoid. Already-answered is not an error and not a second ask: the current
 * capability comes straight back.
 *
 * Both shapes of the API are handled. It returned a permission through a
 * callback long before it returned a promise, and Safari still resolves the
 * promise with `undefined` on some versions — hence the re-read rather than a
 * trust of the resolved value.
 */
export async function requestPlantNotificationPermission(notificationApi = defaultNotificationApi()) {
    const capability = getPlantNotificationCapability(notificationApi);

    if (!capability.isSupported || capability.permission !== NOTIFICATION_STATUS.default) {
        return capability;
    }

    try {
        const permission = await Promise.resolve(notificationApi.requestPermission());

        if (typeof permission === 'string') {
            return { isSupported: true, permission, status: permission };
        }

        return getPlantNotificationCapability(notificationApi);
    } catch (error) {
        return { isSupported: true, permission: null, status: NOTIFICATION_STATUS.error };
    }
}

/**
 * How long to wait for a service worker registration before giving up.
 *
 * `navigator.serviceWorker.ready` is a promise that by specification never
 * REJECTS — it simply stays pending until the origin has an active worker. So an
 * origin whose registration failed (a 404 on the script, a dev server over plain
 * http, a browser with workers disabled) leaves every `await` on it hanging for
 * the life of the page. Unbounded, that wedges the opt-in button: the visitor
 * presses "Notify me", permission is granted, and the confirmation never comes
 * because the await behind it never settles.
 *
 * The same argument `deployedVersionClient` makes for its own timeout: a request
 * that never settles is indistinguishable from an app that stopped trying.
 */
const REGISTRATION_TIMEOUT_MS = 5000;

const REGISTRATION_TIMED_OUT = Symbol('registration-timed-out');

function withTimeout(promise, timeoutMs) {
    let timer = null;

    return Promise.race([
        Promise.resolve(promise),
        new Promise((resolve) => {
            timer = setTimeout(() => resolve(REGISTRATION_TIMED_OUT), timeoutMs);
        }),
    ]).finally(() => clearTimeout(timer));
}

/**
 * The service worker registration a notification is shown THROUGH.
 *
 * `new Notification()` is not an option: it is unavailable in every installed
 * PWA on iOS and is being removed from the platform generally. Going through the
 * registration is also what makes the notification survive the page — and what
 * gives `notificationclick` in the worker something to handle.
 */
export async function getNotificationRegistration({
    navigatorObject = typeof navigator === 'undefined' ? null : navigator,
    timeoutMs = REGISTRATION_TIMEOUT_MS,
} = {}) {
    if (!navigatorObject || !('serviceWorker' in navigatorObject)) {
        return null;
    }

    try {
        if (typeof navigatorObject.serviceWorker.getRegistration === 'function') {
            const existing = await withTimeout(navigatorObject.serviceWorker.getRegistration(), timeoutMs);

            if (existing && existing !== REGISTRATION_TIMED_OUT && typeof existing.showNotification === 'function') {
                return existing;
            }
        }

        const ready = await withTimeout(navigatorObject.serviceWorker.ready, timeoutMs);

        return ready && ready !== REGISTRATION_TIMED_OUT && typeof ready.showNotification === 'function' ? ready : null;
    } catch (error) {
        return null;
    }
}

export { REGISTRATION_TIMEOUT_MS };

/** The icon every notification this app raises is drawn with. */
const NOTIFICATION_ICON = '/assets/icons/icon-192.png';

/**
 * Show one notification, through the registration, swallowing every failure.
 *
 * The single place a notification is raised from the page — the arrival watch
 * and the opt-in confirmation both come through here, so the icon, the badge and
 * the click payload are decided once. A browser that refuses has done nothing
 * this app needs to recover from, and there is no screen to report it on.
 */
export async function showAppNotification({ title, body, tag, url = '/', data = {} } = {}) {
    const registration = await getNotificationRegistration();

    if (!registration || !title) {
        return false;
    }

    try {
        await registration.showNotification(title, {
            body,
            tag,
            icon: NOTIFICATION_ICON,
            badge: NOTIFICATION_ICON,
            // The tag already collapses a repeat; renotify would make the
            // collapsed one buzz again, which is the opposite of the point.
            renotify: false,
            requireInteraction: false,
            data: { ...data, url },
        });

        return true;
    } catch (error) {
        return false;
    }
}
